import { useFlags } from '@atlaskit/flag';
import { AddEditProcessTypeFormFields } from 'core/config/form-fields';
import { useDialog } from 'core/providers/DialogProvider';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import { useState } from 'react';
import { IButton } from 'shared/components/buttons/button-interface';
import { ProcessTypesApiService } from 'core/api';
import { useAuthState } from 'core/providers/AuthProvider';
import { IAddEditProcessTypeFormOutput } from '../types';
import { IProcessTypeDao, IProcessTypeDto } from 'core/api/types/process-types.interface';
import SharedForm from 'shared/components/form/form';
import { ISharedFormField } from 'shared/components/form/form-interface';
import SharedDialogBase from 'shared/components/dialog-base/dialog-base';

const AddEditProcessTypeDialog = ({ processType }: { processType?: IProcessTypeDao }) => {
  const { userData } = useAuthState();
  const flags = useFlags();
  const dialog = useDialog();

  const [formSubmitting, setFormSubmitting] = useState(false);

  const addEditProcessType = async (data: IAddEditProcessTypeFormOutput) => {
    setFormSubmitting(true);

    const payload: IProcessTypeDto = {
      active: data.active,
      name: data.name,
      requireBarcode: data.requireBarcode,
      requireSignature: data.requireSignature,
      requirePhoto: data.requirePhoto,
      isPickupDropoff: data.isPickupDropoff,
      instructions: data.instructions,
    };

    try {
      if (processType) {
        await ProcessTypesApiService.update(processType.uid, payload, {
          fullName: userData?.fullName ?? '',
          uid: userData?.uid ?? '',
        });
      } else {
        await ProcessTypesApiService.set(payload, {
          fullName: userData?.fullName ?? '',
          uid: userData?.uid ?? '',
        });
      }
      showSuccessFlag(
        processType ? 'Process type updated' : 'Process type created',
        `The process type has been ${processType ? 'updated' : 'created'} successfully`,
        flags
      );
      dialog?.closeDialog();
    } catch (error) {
      showErrorFlag(
        processType ? 'Process type update failed' : 'Process type creation failed',
        `Unable to ${processType ? 'update' : 'create'} a process type, please try again.`,
        flags
      );
      setFormSubmitting(false);
    }
  };

  const cancelButton: IButton = {
    onClick: () => dialog?.closeDialog(),
    label: 'Cancel',
    appearance: 'subtle',
    type: 'button',
  };

  const getDefaultValue = (processType: IProcessTypeDao, key: string) => {
    switch (key) {
      default:
        return processType[key as keyof IProcessTypeDao];
    }
  };

  const fields = AddEditProcessTypeFormFields.map((field) => ({
    ...field,
    defaultValue: processType ? getDefaultValue(processType, field.key) : field.defaultValue,
  }));

  const customContent = () => {
    return (
      <SharedForm
        className='p-4 overflow-y-auto'
        onSubmit={addEditProcessType}
        fields={fields as ISharedFormField[]}
        buttonLabel='Submit'
        loading={formSubmitting}
        cancelButton={cancelButton}
      />
    );
  };

  return (
    <SharedDialogBase
      title={processType ? 'Update process type' : 'Add process type'}
      customContentTemplate={customContent()}
      showButtons={false}
    />
  );
};

export default AddEditProcessTypeDialog;
